/// <amd-module name="Wit/Common/PredicateWIT" />

import { caseInsensitiveContains, equals, localeIgnoreCaseComparer, startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { FilterClause } from "Wit/Clients/Work/Contracts/Work";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { getFriendlyDistinctDisplayName, isIdentityRef, isWorkItemIdentityRef } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { buildDate, defaultComparer, stripTimeFromDate } from "./DateUtils";
import { CoreFieldRefNames, FieldType } from "./Generated/Constants";
import { IPredicateEvaluationHelper } from "./Predicate";
import { contains } from "./Utilities/Array";
import { WiqlOperators, getLocalizedOperator } from "./WiqlOperators";
import { parseCurrentIteration } from "./WiqlValues";

// Copied mainly from Tfs/Service/WebAccess/Agile/Scripts/Common/PredicateWIT.ts

export class WorkItemPredicateEvaluationHelper implements IPredicateEvaluationHelper {
    private _fieldValue: any;
    private _fieldType: number | undefined;
    private _userIdentity: IdentityRef | undefined;
    private _pageContext: IVssPageContext;
    private _teamId: string | undefined;

    constructor(pageContext: IVssPageContext, fieldValue: any, fieldType?: number, identity?: IdentityRef, teamId?: string) {
        this._fieldValue = fieldValue;
        this._fieldType = fieldType;
        this._userIdentity = identity;
        this._pageContext = pageContext;
        this._teamId = teamId;
    }

    /**
     * Retrieve the field value corresponding to fieldReferenceName, and compare it with 'value'
     * @param fieldReferenceName The reference name of the field to retrieved
     * @param value The value to be compared with.
     * @return A number indicating if the value is lesser ( < 0) , greater ( > 0) or equal ( == 0)
     */
    public async compare(fieldReferenceName: string, value: any): Promise<number> {
        if (typeof value === "string") {
            value = value.trim();
        }
        switch (this._fieldType) {
            case FieldType.PlainText:
            case FieldType.String:
                this._fieldValue = extractDistinctDisplayNameOrString(this._fieldValue);
                value =
                    this._userIdentity && equals(value, getLocalizedOperator(WiqlOperators.MacroMe), true)
                        ? getFriendlyDistinctDisplayName(this._userIdentity)
                        : value;
                return localeIgnoreCaseComparer(this._getFieldValue(fieldReferenceName), value);
            case FieldType.TreePath:
                const ciParts = parseCurrentIteration(value, /* useInvariantMacro */ true);
                if (ciParts) {
                    value = await this._getCurrentIteration(ciParts.offset, this._teamId);
                }

                return localeIgnoreCaseComparer(this._getFieldValue(fieldReferenceName), value);
            case FieldType.Integer:
            case FieldType.Double:
                const fieldValueStr = this._fieldValue?.toString() ?? "";
                const valueStr = value?.toString() ?? "";
                // if both the field-value string and value string are "" (both are unset), they are considered same.
                if (fieldValueStr === "" && valueStr === "") {
                    return 0;
                } else if (fieldValueStr === "") {
                    // if the field-value string is "" but value string is not "", consider value as bigger.
                    return -1;
                } else if (valueStr === "") {
                    // if the field-value string is not "" but value string is "", consider field-value as bigger.
                    return 1;
                }
                const actualNumber: number = Number(fieldValueStr);
                const searchNumber: number = Number(valueStr);
                if (isNaN(actualNumber) || isNaN(searchNumber)) {
                    return -1;
                }
                return actualNumber - searchNumber;
            case FieldType.DateTime:
                let actualValue = this._getFieldValue(fieldReferenceName);
                // if both actual and search value are unset we need to evaluate it to matching=true
                // do this before converting it to a date object
                if (!actualValue && !value) {
                    return 0;
                } else if (!actualValue) {
                    return Number.NEGATIVE_INFINITY;
                } else if (!value) {
                    return 1;
                }

                // Convert macro to actual date
                value = new Date(buildDate(value).toISOString());

                if (actualValue?.value instanceof Date) {
                    actualValue = actualValue.value;
                }

                if (actualValue instanceof Date) {
                    const actualDateValue = stripTimeFromDate(actualValue);
                    const searchDate = stripTimeFromDate(value);

                    return defaultComparer(actualDateValue, searchDate);
                }
                return -1;
            case FieldType.Boolean:
                const actualBoolValue: string = this._getFieldValue(fieldReferenceName) === true ? "true" : "false";
                const searchBoolValue: string = value;
                if (actualBoolValue === searchBoolValue.toLowerCase()) {
                    return 0;
                } else {
                    return 1;
                }
        }
        return -1;
    }

    /**
     * Retrieve the field value corresponding to fieldReferenceName, and check if 'value' is contained within it.
     * @param fieldReferenceName The reference name of the field to retrieved
     * @param value The value to be compared with.
     * @return A boolean indicating if the value is contained within the field or not.
     */
    public contains(fieldReferenceName: string, value: any): boolean {
        switch (this._fieldType) {
            case FieldType.PlainText:
            case FieldType.String:
                if (fieldReferenceName === CoreFieldRefNames.Tags) {
                    const fieldValue = this._getFieldValue(fieldReferenceName);
                    const tags = fieldValue?.split(";") ?? [];

                    if (value?.trim() === "" && tags.length === 0) {
                        return true;
                    }

                    tags.forEach((tag: string, index: number) => {
                        tags[index] = tag.trim().toUpperCase();
                    });

                    return contains(tags, value.toUpperCase());
                }
            case FieldType.TreePath:
                const fieldValue = this._getFieldValue(fieldReferenceName);
                if (fieldValue) {
                    return caseInsensitiveContains(fieldValue, value);
                }
        }
        return false;
    }

    /**
     * Retrieve the field value corresponding to the TreePath fieldReferenceName, and check if 'value' is 'UNDER' within it.
     * @param fieldReferenceName The reference name of the field to retrieved
     * @param value The value to be compared with.
     * @return A boolean indicating if the value is 'UNDER' the TreePath field or not.
     */
    public under(fieldReferenceName: string, value: any): boolean {
        switch (this._fieldType) {
            case FieldType.TreePath:
                let fieldValue = this._getFieldValue(fieldReferenceName);
                fieldValue = this._trimTrailingSlashes(fieldValue);
                value = this._trimTrailingSlashes(value);
                return startsWith(fieldValue, value, true);
        }
        return false;
    }

    private _trimTrailingSlashes(path: string): string {
        if (!path) {
            return "";
        }

        return path.replace(/(\\)+$/, "");
    }

    private _getFieldValue(fieldReferenceName: string): any {
        if (this._fieldValue) {
            return this._fieldValue;
        }

        return null;
    }

    private async _getCurrentIteration(offset: number, teamId?: string): Promise<string> {
        const teamService = this._pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamService.getTeamSettings(teamId ? { ignoreCache: true } : undefined, teamId);

        if (offset > 0) {
            const iteration = teamSettings.futureIterations[offset - 1];
            return iteration && iteration.friendlyPath;
        }

        if (offset < 0) {
            const idx = teamSettings.previousIterations.length + offset;
            const iteration = teamSettings.previousIterations[idx];
            return iteration && iteration.friendlyPath;
        }

        return teamSettings.currentIteration?.friendlyPath;
    }
}

export interface IItemQuery {
    clauses: FilterClause[];
    groups: IQueryGroup[];
    maxGroupLevel: number;
}

export interface IQueryGroup {
    end: number;
    start: number;
    level: number;
}

export function getWiql(query: IItemQuery): string {
    let wiql = "";
    if (!isQueryValid) {
        return wiql;
    }

    if (query && query.clauses) {
        const groups = query.groups;
        let parens = "";

        const length = query.clauses.length;
        for (let i = 0; i < length; i++) {
            if (wiql && query.clauses[i].logicalOperator) {
                wiql = wiql + " " + query.clauses[i].logicalOperator + " ";
            }

            if (groups && groups.length) {
                parens = "";
                groups.forEach(group => {
                    if (group.start === i + 1) {
                        parens += "(";
                    }
                });

                if (parens) {
                    wiql = wiql + parens;
                }
            }

            if (query.clauses[i].fieldName && query.clauses[i].operator) {
                wiql = wiql + "[" + query.clauses[i].fieldName + "]";
                wiql = wiql + " " + query.clauses[i].operator;

                let clauseValue = query.clauses[i].value;
                clauseValue = clauseValue.replace(/\'/g, "''");
                clauseValue = clauseValue.trim();

                // Macro values are not surrounded with single-quotes, in alignment with query behavior.
                if (!clauseValue.startsWith(WiqlOperators.MacroStart)) {
                    clauseValue = " '" + clauseValue + "'";
                }
                wiql = wiql + clauseValue;
            }

            if (groups && groups.length) {
                parens = "";
                groups.forEach(group => {
                    if (group.end === i + 1) {
                        parens += ")";
                    }
                });

                if (parens) {
                    wiql = wiql + parens;
                }
            }
        }
    }

    return wiql;
}

export function isQueryValid(query: IItemQuery): boolean {
    const parenStack: string[] = [];
    let isBalanced = true;

    if (query && query.clauses) {
        const groups = query.groups;
        for (let i = 0; i < query.clauses.length; i++) {
            if (groups && groups.length) {
                groups.forEach(group => {
                    // If clauses[i] has an open parentheses
                    if (group.start === i + 1) {
                        parenStack.push("(");
                    }
                    // If clauses[i] has a close parentheses
                    if (group.end === i + 1) {
                        // If there is an ending parenthesis without a pair then return false. Eg: A) OR B
                        // Pop the top element from stack, if it is not a pair parentheses of character then there is a mismatch.
                        if (parenStack.length <= 0 || !(parenStack.pop() === "(")) {
                            isBalanced = false;
                            return;
                        }
                    }
                });
            }
        }

        // If there is something left in expression then there is a starting parentheses without a closing parenthesis.Eg: (A OR B
        if (parenStack.length > 0 || !isBalanced) {
            return false;
        }
    }

    return true;
}

function extractDistinctDisplayNameOrString(maybeIdentity: any): string | null | undefined {
    if (isIdentityRef(maybeIdentity)) {
        return getFriendlyDistinctDisplayName(maybeIdentity);
    }
    if (isWorkItemIdentityRef(maybeIdentity)) {
        if (maybeIdentity.distinctDisplayName) {
            return maybeIdentity.distinctDisplayName;
        } else {
            return getFriendlyDistinctDisplayName(maybeIdentity.identityRef);
        }
    }

    if (typeof maybeIdentity === "string") {
        return maybeIdentity;
    }

    return maybeIdentity?.toString();
}
